import React, { useEffect, useState } from 'react';
import { Input, Select, Button, DatePicker, InputNumber, Row, Col } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import dayjs from 'dayjs';
import type { TransactionLogsFiltersValues } from '../../../services/transactionLogsService';
import { INTERNAL_STATUS_FILTER_OPTIONS } from '../../../utils/transactionLogStatusLabels';
import { spacing } from '../../../design-system/tokens';

const { RangePicker } = DatePicker;

interface TransactionLogsFiltersProps {
  values: TransactionLogsFiltersValues;
  onChange: (values: TransactionLogsFiltersValues) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  /** Khi set: cố định merchant (ẩn dropdown), dùng trên trang chi tiết rút tiền */
  lockedMerchantId?: number;
}

const TRANSACTION_TYPE_OPTIONS = [
  { value: '', label: 'Tất cả loại' },
  { value: 'payment', label: 'Thanh toán' },
  { value: 'refund', label: 'Hoàn tiền' },
];

const PAYMENT_METHOD_OPTIONS = [
  { value: '', label: 'Tất cả' },
  { value: 'VN_MOMO', label: 'Momo' },
  { value: 'VN_ZALOPAY', label: 'ZaloPay' },
  { value: 'VN_VIETQR', label: 'VietQR' },
  { value: 'VN_VNPAY', label: 'VNPay' },
  { value: 'VN_VIETTELPAY', label: 'ViettelPay' },
  { value: 'VN_BANK_TRANSFER', label: 'Chuyển khoản' },
  { value: 'VN_CREDIT_CARD', label: 'Thẻ tín dụng' },
  { value: 'VN_DEBIT_CARD', label: 'Thẻ ghi nợ' },
];

export const TransactionLogsFilters: React.FC<TransactionLogsFiltersProps> = ({
  values,
  onChange,
  onSearch,
  onReset,
  loading,
  lockedMerchantId,
}) => {
  const [merchants, setMerchants] = useState<
    Array<{ id: number; name: string }>
  >([]);

  useEffect(() => {
    api
      .get<{ success: boolean; data?: Array<{ id: number; name: string }> }>(
        '/restaurants'
      )
      .then(res => {
        const data = res.data?.data;
        if (Array.isArray(data)) {
          setMerchants(
            data.map((m: { id: number; name: string }) => ({
              id: m.id,
              name: m.name,
            }))
          );
        }
      })
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      .catch(() => {});
  }, []);

  const dateRange =
    values.from && values.to
      ? ([dayjs(values.from), dayjs(values.to)] as [dayjs.Dayjs, dayjs.Dayjs])
      : undefined;

  return (
    <div
      style={{ padding: spacing.md, background: '#fafafa', borderRadius: 12 }}
    >
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Tìm kiếm
          </div>
          <Input
            placeholder="Transaction ID, Order ID, Order code, Reference, Merchant, Customer..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={values.q ?? ''}
            onChange={e => onChange({ ...values, q: e.target.value })}
            onPressEnter={onSearch}
            allowClear
            size="large"
            style={{ width: '100%', maxWidth: 480 }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Khoảng thời gian
          </div>
          <RangePicker
            placeholder={['Từ ngày', 'Đến ngày']}
            value={dateRange}
            onChange={range =>
              onChange({
                ...values,
                from: range?.[0]?.format('YYYY-MM-DD'),
                to: range?.[1]?.format('YYYY-MM-DD'),
              })
            }
            style={{ width: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Loại giao dịch
          </div>
          <Select
            placeholder="Tất cả loại"
            value={values.transactionType ?? ''}
            onChange={v =>
              onChange({ ...values, transactionType: v || undefined })
            }
            style={{ width: '100%' }}
            options={TRANSACTION_TYPE_OPTIONS}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={12} sm={8} md={6}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Phương thức
          </div>
          <Select
            placeholder="Tất cả"
            value={values.paymentMethod ?? ''}
            onChange={v =>
              onChange({ ...values, paymentMethod: v || undefined })
            }
            style={{ width: '100%' }}
            options={PAYMENT_METHOD_OPTIONS}
          />
        </Col>
        <Col xs={12} sm={8} md={6}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Trạng thái nội bộ
          </div>
          <Select
            placeholder="Tất cả"
            value={values.internalStatus ?? ''}
            onChange={v =>
              onChange({ ...values, internalStatus: v || undefined })
            }
            style={{ width: '100%' }}
            options={INTERNAL_STATUS_FILTER_OPTIONS}
          />
        </Col>
        {lockedMerchantId == null ? (
          <Col xs={12} sm={8} md={6}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
              Merchant
            </div>
            <Select
              placeholder="Tất cả"
              value={values.merchantId ?? ''}
              onChange={v =>
                onChange({
                  ...values,
                  merchantId: v === '' ? undefined : (v as number),
                })
              }
              style={{ width: '100%' }}
              showSearch
              optionFilterProp="label"
              options={[
                { value: '', label: 'Tất cả' },
                ...merchants.map(m => ({
                  value: m.id,
                  label: `${m.name} (${m.id})`,
                })),
              ]}
            />
          </Col>
        ) : (
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
              Quán (khóa theo yêu cầu rút tiền)
            </div>
            <Input
              readOnly
              value={`ID ${lockedMerchantId}`}
              style={{ width: '100%' }}
            />
          </Col>
        )}
        <Col xs={12} sm={8} md={4}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Số tiền min
          </div>
          <InputNumber
            placeholder="Min"
            value={values.amountMin}
            onChange={v => onChange({ ...values, amountMin: v ?? undefined })}
            style={{ width: '100%' }}
            min={0}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Số tiền max
          </div>
          <InputNumber
            placeholder="Max"
            value={values.amountMax}
            onChange={v => onChange({ ...values, amountMax: v ?? undefined })}
            style={{ width: '100%' }}
            min={0}
            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Webhook
          </div>
          <Select
            placeholder="Tất cả"
            value={
              values.hasWebhook === undefined
                ? ''
                : values.hasWebhook
                ? 'yes'
                : 'no'
            }
            onChange={v =>
              onChange({
                ...values,
                hasWebhook: v === 'yes' ? true : v === 'no' ? false : undefined,
              })
            }
            style={{ width: '100%' }}
            options={[
              { value: '', label: 'Tất cả' },
              { value: 'yes', label: 'Có' },
              { value: 'no', label: 'Không' },
            ]}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Chỉ thất bại
          </div>
          <Select
            placeholder="Tất cả"
            value={values.failedOnly ? 'yes' : ''}
            onChange={v => onChange({ ...values, failedOnly: v === 'yes' })}
            style={{ width: '100%' }}
            options={[
              { value: '', label: 'Tất cả' },
              { value: 'yes', label: 'Chỉ thất bại' },
            ]}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#8c8c8c' }}>
            Chỉ hoàn tiền
          </div>
          <Select
            placeholder="Tất cả"
            value={values.refundedOnly ? 'yes' : ''}
            onChange={v => onChange({ ...values, refundedOnly: v === 'yes' })}
            style={{ width: '100%' }}
            options={[
              { value: '', label: 'Tất cả' },
              { value: 'yes', label: 'Chỉ hoàn tiền' },
            ]}
          />
        </Col>
      </Row>

      <div
        style={{
          marginTop: 20,
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <Button
          type="primary"
          onClick={onSearch}
          loading={loading}
          size="large"
        >
          Tìm kiếm
        </Button>
        <Button icon={<ReloadOutlined />} onClick={onReset} size="large">
          Đặt lại
        </Button>
      </div>
    </div>
  );
};
